import React, { useEffect, useMemo, useRef, useState } from "react";
import UAUCAllPanel from "./UAUCAllPanel";
import UAUCUnresolvedPanel from "./UAUCUnresolvedPanel";
import TableLoader from "../TableComponent/TableLoader";
import MeetingAllPanel from "./MeetingAllPanel";
import InductionAllPanel from "./InductionAllPanel";
import SpecificMeetingAllPanel from "./SpecificMeetingAllPanel";
import AdminUserAnalytics from "./AdminUserAnalytics";

const DEFAULT_API_BASE = "https://lttsbackend.axiomos.in";
const DEFAULT_PROJECTS_ENDPOINT = "/projects/summary/all";

async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`,
    );
  return data;
}

// --- Modal ---
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-6xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// --- Project Card ---
function ProjectCard({ project, counts, onClick }) {
  return (
    <div
      onClick={() => onClick?.(project)}
      className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="text-xl font-semibold">
          {project.projectName || "Untitled Project"}
        </div>
        <span className="rounded-full px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
          View
        </span>
      </div>
      {project.siteLocation && (
        <div className="mt-2 text-sm text-gray-500">{project.siteLocation}</div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm text-gray-600">
        {Object.entries(counts || {}).map(([key, value]) => (
          <div
            key={key}
            className="flex flex-col rounded-xl bg-gray-50 px-3 py-2"
          >
            <div className="text-[11px] uppercase tracking-wide text-gray-400">
              {key}
            </div>
            <div className="font-bold">{value}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-2 text-[13px] text-indigo-600">
        <span className="transition group-hover:translate-x-0.5">
          Open details
        </span>{" "}
        →
      </div>
    </div>
  );
}

// --- Project Meta ---
function ProjectMetaGrid({ project }) {
  return (
    <div className="mb-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
      {project.projectId && (
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            Code
          </div>
          <div className="font-medium">{project.projectId}</div>
        </div>
      )}
      {project.siteLocation && (
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            Location
          </div>
          <div className="font-medium">{project.siteLocation}</div>
        </div>
      )}
      {project.status && (
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            Status
          </div>
          <div className="font-medium">{project.status}</div>
        </div>
      )}
    </div>
  );
}

// --- Main Component ---
export default function ProjectSiteSummary({
  apiBase = DEFAULT_API_BASE,
  projectsEndpoint = DEFAULT_PROJECTS_ENDPOINT,
  title = "Project Dashboard",
  subtitle = "Click a project to view site-wise totals for safety and compliance modules.",
}) {
  const [state, setState] = useState({ projects: [], loading: true, err: "" });
  const [query, setQuery] = useState("");
  const [activeProject, setActiveProject] = useState(null);
  const [activeEntity, setActiveEntity] = useState(null);
  const abortRef = useRef(null);

  // --- Fetch Projects ---
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    (async () => {
      try {
        setState((s) => ({ ...s, loading: true, err: "" }));
        const data = await safeFetchJSON(apiBase + projectsEndpoint, {
          signal: ac.signal,
        });
        setState({ projects: data || [], loading: false, err: "" });
      } catch (e) {
        if (e.name !== "AbortError") {
          setState({
            projects: [],
            loading: false,
            err: e.message || "Failed to load projects.",
          });
        }
      }
    })();
    return () => ac.abort();
  }, [apiBase, projectsEndpoint]);

  const { projects, loading, err } = state;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      [
        p.project?.projectName,
        p.project?.siteLocation,
        p.project?.projectId,
        p.project?.company,
      ].some((field) => (field || "").toLowerCase().includes(q)),
    );
  }, [projects, query]);

  const normalized = (activeEntity || "").toLowerCase();

  return (
    <div className="mx-auto w-5/5 px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
        </div>
        <div className="w-full sm:w-80">
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, code, company, city..."
              className="w-full rounded-2xl border border-gray-300 px-4 py-2.5 pr-10 shadow-sm focus:border-indigo-500 focus:ring-1"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔎
            </span>
          </div>
        </div>
      </div>

      <AdminUserAnalytics />

      {/* Content */}
      {loading ? (
        <TableLoader />
      ) : err ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {err}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
          No projects found.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => (
            <ProjectCard
              key={item.project._id}
              project={item.project}
              counts={item.counts}
              onClick={() => {
                setActiveProject(item.project);
                setActiveEntity(null);
              }}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <Modal
        open={!!activeProject}
        onClose={() => {
          setActiveProject(null);
          setActiveEntity(null);
        }}
        title={
          activeProject
            ? `${activeProject.projectName || "Project"} — ${
                normalized === "uaucunresolved"
                  ? "Observation Unresolved"
                  : normalized === "uauc"
                    ? "Observations"
                    : normalized === "tbtmeeting"
                      ? "TBT Meetings"
                      : normalized === "specifictraining"
                        ? "Specific Training"
                        : normalized === "induction"
                          ? "Inductions"
                          : "Site Summary"
              }`
            : ""
        }
      >
        {activeProject && (
          <>
            <ProjectMetaGrid project={activeProject} />

            {!activeEntity && (
              <div className="mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(
                    projects.find((p) => p.project._id === activeProject._id)
                      ?.counts || {},
                  ).map(([key, value]) => {
                    const normKey =
                      key === "tbtMeeting"
                        ? "tbtmeeting"
                        : key === "specificMeeting"
                          ? "specificTraining" // ✅ match to your modal condition
                          : key === "inductionMeeting"
                            ? "induction"
                            : key;

                    const clickable = [
                      "uauc",
                      "uaucUnresolved",
                      "tbtmeeting",
                      "specificTraining",
                      "induction",
                    ].includes(normKey);

                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={!clickable}
                        onClick={() =>
                          clickable && setActiveEntity(normKey.toLowerCase())
                        }
                        className={`flex flex-col justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ${
                          clickable
                            ? "hover:shadow-md hover:-translate-y-0.5 transition"
                            : ""
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-600">
                          {normKey.toLowerCase() === "uauc"
                            ? "Observations"
                            : normKey.toLowerCase() === "uaucunresolved"
                              ? "Observation Unresolved"
                              : normKey}
                        </div>

                        <div className="mt-3 text-3xl font-bold tabular-nums">
                          {value}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Panels */}
            {normalized === "tbtmeeting" && (
              <MeetingAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "specifictraining" && (
              <SpecificMeetingAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "induction" && (
              <InductionAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "uauc" && (
              <UAUCAllPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}

            {normalized === "uaucunresolved" && (
              <UAUCUnresolvedPanel
                apiBase={apiBase}
                projectId={activeProject._id}
                onBack={() => setActiveEntity(null)}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
